import React, { useState, useEffect } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import api from '@/utils/api.utils';
import { ToastService } from '@/utils/toast';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  schoolName?: string;
}

const InfoIcon = IoInformationCircleOutline as React.ComponentType<{ className?: string }>;

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  user,
  schoolName
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [newPrompts, setNewPrompts] = useState(true);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState(false);
  const [decisionsReleased, setDecisionsReleased] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [savedPhoneNumbers, setSavedPhoneNumbers] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && schoolName) {
      setIsLoading(false);      
      const savedNumbers = localStorage.getItem('savedPhoneNumbers');
      if (savedNumbers) {  
        const numbers = JSON.parse(savedNumbers);
        setSavedPhoneNumbers(numbers);
      }

      const settingsKey = `notificationSettings_${schoolName}`;
      const savedSettings = localStorage.getItem(settingsKey);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setPhoneNumber(settings.phoneNumber || '');
        setNewPrompts(settings.newPrompts !== undefined ? settings.newPrompts : true);
        setUpcomingDeadlines(settings.upcomingDeadlines !== undefined ? settings.upcomingDeadlines : false);
        setDecisionsReleased(settings.decisionsReleased !== undefined ? settings.decisionsReleased : true);
      } else {
        setPhoneNumber('');
        setNewPrompts(true);
        setUpcomingDeadlines(false);
        setDecisionsReleased(true);
      }
    }
  }, [isOpen, schoolName]);

  const savePhoneNumber = (number: string) => {
    if (!number.trim()) return;
    
    const currentSaved = [...savedPhoneNumbers];
    const filtered = currentSaved.filter(num => num !== number);
    const updated = [number, ...filtered].slice(0, 5);
    
    setSavedPhoneNumbers(updated);
    localStorage.setItem('savedPhoneNumbers', JSON.stringify(updated));
  };

  const saveUniversitySettings = () => {
    if (!schoolName) return;
    
    const settings = {
      phoneNumber,
      newPrompts,
      upcomingDeadlines,
      decisionsReleased,
      lastUpdated: new Date().toISOString()
    };
    
    const settingsKey = `notificationSettings_${schoolName}`;
    localStorage.setItem(settingsKey, JSON.stringify(settings));
  };

  if (!isOpen) {
    return null;
  }

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const notificationData = {
        userName: user?.name || user?.displayName || '',
        email: user?.email || '',
        schoolName: schoolName || '',
        phoneNumber: phoneNumber,
        newPrompts: newPrompts,
        upcomingDeadlines: upcomingDeadlines,
        decisionsReleased: decisionsReleased
      };

      const response = await api.post('/api/v1/notifications/webhook', notificationData);

      if (response.data.success) {
        savePhoneNumber(phoneNumber);
        saveUniversitySettings();
        setIsLoading(false);
        ToastService.showSuccess(`Notification settings saved for ${schoolName}!`);
        onClose();
      } else {
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="mb-6 pr-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Notification settings
          </h3>
          {schoolName && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              for {schoolName}
            </p>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone number:
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number"
              list="phone-numbers"
              autoComplete="tel"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-list-button]:hidden"
            />
            {savedPhoneNumbers.length > 0 && (
              <datalist id="phone-numbers">
                {savedPhoneNumbers.map((number, index) => (
                  <option key={index} value={number} />
                ))}
              </datalist>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">New prompts</span>
                <div 
                  className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                  title="Receive texts when this school releases new essay prompts"
                >
                  <InfoIcon className="w-4 h-4" />
                </div>
              </div>
              <button
                onClick={() => setNewPrompts(!newPrompts)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  newPrompts ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    newPrompts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Upcoming deadlines</span>
                <div 
                  className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                  title="Receive period texts about upcoming application deadlines for this school"
                >
                  <InfoIcon className="w-4 h-4" />
                </div>
              </div>
              <button
                onClick={() => setUpcomingDeadlines(!upcomingDeadlines)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  upcomingDeadlines ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    upcomingDeadlines ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Decisions released</span>
                <div 
                  className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                  title="Get notified when this school releases a decision on your application"
                >
                  <InfoIcon className="w-4 h-4" />
                </div>
              </div>
              <button
                onClick={() => setDecisionsReleased(!decisionsReleased)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  decisionsReleased ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    decisionsReleased ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            We never sell or share your personal contact information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;